<template>
  <div>
    <b-table
      ref="table"
      class="bg-white"
      small
      responsive
      show-empty
      :items="items"
      :fields="fields"
      :sort-compare="sortCompare"
      :per-page="perPage"
      :current-page="currentPage"
    >
      <template #empty>
        <p align="center">{{ trans('no_booking_found') }}</p>
      </template>
      <template #cell(index)="data">
        <small class="text-muted">{{ (currentPage > 0 ? (currentPage-1) : 0)*(perPage || 0) + data.index + 1 }}</small>
      </template>
      <template #cell(package_name)="data">
        {{ data.value }}
        <sup>
          <b-badge v-if="bookingWasModifiedByAdmin(data.item)" variant="primary" :title="trans('admin-modified')" v-b-tooltip.hover>{{ trans('admin') }}</b-badge>
          <booking-status-badge v-model="data.item.status"></booking-status-badge>
        </sup>
        <small v-if="canModify" class="d-block">
          <b-button v-if="!data.item.notes" variant="link" size="sm" class="py-0 text-muted" @click="onClickEditNotes(data.item)">
            <i class="fa fa-fw fa-comment-alt"></i>
          </b-button>
          <b-button v-if="!!data.item.notes" variant="link" size="sm" class="py-0 text-success" @click="onClickEditNotes(data.item)">
            <i class="fa fa-fw fa-comment-alt"></i>
          </b-button>
        </small>
      </template>
      <template #cell(events)="data">
        <div v-if="isDeleted(data.item) || data.item.name.indexOf('@') === -1">{{ data.item.name }}</div>
        <booking-events-schedule
          v-if="!isDeleted(data.item)"
          :organization="organization"
          :events="data.value"
        ></booking-events-schedule>
        <div v-if="canModify">
          <b-button
            variant="link"
            size="sm"
            :title="trans('modify')"
            @click="onClickModify(data.item)"
            :disabled="busy"
          >
            <i class="fa fa-fw fa-edit"></i>
            {{ trans('modify') }}
          </b-button>
          <delete-button
            v-if="!isDeleted(data.item)"
            variant="link"
            size="sm"
            button-class="text-danger"
            @confirm="onClickDelete(data.item)"
            :disabled="busy"
          >
            <i class="fa fa-fw fa-times"></i>
            {{ trans('delete') }}
          </delete-button>
          <b-button
            v-if="canEndNow(data.item)"
            variant="link"
            size="sm"
            class="text-danger"
            :title="trans('end_now')"
            @click="onClickEndNow(data.item)"
            :disabled="busy"
          >
            {{ trans('end') }}
          </b-button>
        </div>
      </template>
      <template #cell(tracking_status)="data">
        <booking-tracking-status-button
          v-if="canModify"
          :organization="organization"
          v-model="data.item"
          @input="$emit('update-invoice-item', $event)"
          :disabled="busy"
        ></booking-tracking-status-button>
        <open-turnstile-button
          v-if="false && organization.has_access_control"
          :organization="organization"
          size="sm"
          class-name="ml-4"
          :disabled="busy"
        ></open-turnstile-button>
      </template>
    </b-table>
    <b-row v-if="showPagination" class="mb-4">
      <b-col class="col-auto pt-1">
        <small>
          {{ items.length }}
          {{ trans('booking' + (items.length > 1 || items.length == 0 && lang == 'en' ? 's' : '')).toLowerCase() }}
        </small>
      </b-col>
      <b-col class="col-auto">
        <b-pagination
          size="sm"
          :per-page="perPage"
          :total-rows="items.length"
          :value="currentPage"
          @input="$emit('current-page', $event)"
          class="mb-2"
        ></b-pagination>
      </b-col>
      <b-col class="col-3 col-lg-2 col-xl-1">
        <b-form-group class="mb-2">
          <b-form-select
            :value="perPage"
            @input="$emit('per-page', $event)"
            size="sm"
            :options="perPageOptions"
            :disabled="busy"
          ></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>
    <booking-modal
      :organization="organization"
      :booking="booking"
      :invoiceItem="bookingInvoiceItem"
      @saved="$emit('update', $event)"
      @deleted="$emit('update')"
    ></booking-modal>
    <html-textarea-modal
      id="client-invoice-bookings-table-notes-modal"
      :value="!!booking ? booking.notes : null"
      :title="trans('notes_customer')"
      :label="trans('notes_customer_about')"
      attr="notes"
      :organization="organization"
      :url="!!booking ? `/organization/${organization.id}/booking/${booking.id}/notes` : null"
      @input="onSaveNotes"
    ></html-textarea-modal>
  </div>
</template>

<script>
  import HtmlTextareaModal from './../../../common/components/HtmlTextareaModal'
  import BookingStatusBadge from './../booking/BookingStatusBadge'
  import BookingModal from './../booking/BookingModal'
  import BookingTrackingStatusButton from './../booking/BookingTrackingStatusButton'
  import BookingEventsSchedule from './../booking/BookingEventsSchedule'
  import OpenTurnstileButton from './../access/OpenTurnstileButton'
  import DeleteButton from './../../../common/components/DeleteConfirmButton'

  export default {
    props: {
      organization: Object,
      bookings: Array,
      invoice: Object, // Can be null
      noTracking: {
        type: Boolean,
        default: false,
      },
      // Leave these as undefined for no pagination
      perPage: Number,
      perPageOptions: Array,
      currentPage: Number,
    },
    components: {
      HtmlTextareaModal,
      BookingStatusBadge,
      BookingModal,
      BookingTrackingStatusButton,
      BookingEventsSchedule,
      OpenTurnstileButton,
      DeleteButton,
    },
    data() {
      return {
        trans: trans,
        lang: lang,
        moment: moment,
        busy: false,
        error: null,
        fields: _.compact([
          {
            key: 'index',
            label: '',
          },
          {
            key: 'package_name',
            label: trans('package'),
            sortable: true,
          },
          {
            key: 'events',
            label: trans('bookings'),
            sortable: true,
          },
          this.noTracking ? null : {
            key: 'tracking_status',
            label: trans('tracking'),
            sortable: true,
          },
        ]),
        items: _.cloneDeep(this.bookings),
        booking: null,
      }
    },
    computed: {
      showPagination() {
        return this.perPage !== undefined &&
          this.perPageOptions !== undefined &&
          this.currentPage !== undefined
      },
      canModify() {
        return ['owner', 'admin', 'attendant'].indexOf(this.organization.auth_user.permission) >= 0
      },
      bookingInvoiceItem() {
        if (!this.invoice || !this.booking || !this.booking.invoice_item_id) return null
        return _.find(this.invoice.invoice_items, {id: this.booking.invoice_item_id})
      },
    },
    watch: {
      bookings() {
        this.items = _.cloneDeep(this.bookings)
      },
    },
    methods: {
      onClickEditNotes(booking) {
        this.booking = booking
        this.$bvModal.show('client-invoice-bookings-table-notes-modal')
      },
      onSaveNotes(notes) {
        this.booking.notes = notes
        this.booking = null
      },
      sortCompare(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) {
        const a = aRow[key]
        const b = bRow[key]
        if (key == 'events' && !!aRow.start && !!bRow.start) {
          return _.bTableToString(aRow.start).localeCompare(_.bTableToString(bRow.start), compareLocale, compareOptions)
        } else {
          return _.bTableToString(a).localeCompare(_.bTableToString(b), compareLocale, compareOptions)
        }
      },
      canEndNow(item) {
        return _.reduce(item.events, (ok, e) => {
            if (moment().isBefore(e.start)) ok = false
            return ok
          }, true) &&
          _.reduce(item.events, (ok, e) => {
            if (!!e.end && moment().isBefore(e.end)) ok = true
            return ok
          }, false)
      },
      onClickEndNow(item) {
        this.$bvModal.msgBoxConfirm(trans('booking_end_now_about'), {
          title: trans('end_now'),
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: trans('end_now'),
          cancelTitle: trans('cancel'),
        })
        .then(value => {
          if (!value) return

          this.busy = true
          this.error = null
          let url = `/organization/${this.organization.id}/booking/${item.id}/end_now`
          axios.post(url)
          .then(response => {
            this.busy = false
            this.$emit('update')
          })
          .catch(error => {
            this.busy = false
            this.error = _.errorFromAjax(error)
          })
        })
      },
      isDeleted(item) {
        return !item.events || item.events.length == 0
      },
      bookingWasModifiedByAdmin(booking) {
        return _.reduce(booking.log || [], (result, log) => {
          if (log.operation == 'update') result = true
          return result
        }, false)
      },
      onClickModify(booking) {
        this.booking = booking
        this.$nextTick(() => {
          this.$bvModal.show('booking-modal')
        })
      },
      onClickDelete(booking) {
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/booking/${booking.id}`
        axios.delete(url)
        .then(response => {
          this.busy = false
          this.$emit('update')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.bookingsTable = this
    },
  }
</script>